/**
 * 设备管理API
 */
import request from '@/utils/request'

/**
 * 获取设备列表
 * @param {Object} params - 查询参数
 * @param {string} params.keywords - 搜索关键词 (设备名称/编码/MAC)
 * @param {number} params.status - 设备状态 (0: 离线, 1: 在线, 2: 异常)
 * @param {number} params.page - 页码
 * @param {number} params.limit - 每页条数
 * @returns {Promise}
 */
export function getDeviceList(params = {}) {
  return request({
    url: '/admin/device/list',
    method: 'get',
    params
  })
}

/**
 * 获取设备详情
 * @param {string} id - 设备ID
 * @returns {Promise}
 */
export function getDeviceDetail(id) {
  return request({
    url: `/admin/device/${id}`,
    method: 'get'
  })
}

/**
 * 创建设备
 * @param {Object} data - 设备数据
 * @param {string} data.deviceName - 设备名称
 * @param {string} data.board - 设备型号
 * @param {string} data.macAddress - MAC地址
 * @param {string} data.appVersion - 固件版本
 * @param {string} data.deviceRemark - 备注信息
 * @returns {Promise}
 */
export function createDevice(data) {
  return request({
    url: '/admin/device',
    method: 'post',
    data
  })
}

/**
 * 更新设备信息
 * @param {number} id - 设备ID
 * @param {Object} data - 设备数据
 * @returns {Promise}
 */
export function updateDevice(id, data) {
  return request({
    url: `/admin/device/${id}`,
    method: 'put',
    data
  })
}

/**
 * 删除设备
 * @param {number} id - 设备ID
 * @returns {Promise}
 */
export function deleteDevice(id) {
  return request({
    url: `/admin/device/${id}`,
    method: 'delete'
  })
}

/**
 * 设备绑定
 * @param {number} id - 设备ID
 * @param {number} userId - 用户ID
 * @returns {Promise}
 */
export function bindDevice(id, userId) {
  return request({
    url: `/admin/device/${id}/bind`,
    method: 'post',
    data: { userId }
  })
}

/**
 * 设备解绑
 * @param {string} id - 设备ID
 * @param {number} userId - 用户ID
 * @returns {Promise}
 */
export function unbindDevice(id, userId) {
  return request({
    url: `/admin/device/${encodeURIComponent(id)}/unbind`,
    method: 'post',
    data: { userId }
  })
}

/**
 * 固件升级
 * @param {number} id - 设备ID
 * @param {string} appVersion - 目标固件版本
 * @returns {Promise}
 */
export function upgradeFirmware(id, appVersion) {
  return request({
    url: `/admin/device/${id}/firmware`,
    method: 'post',
    data: { appVersion }
  })
}

/**
 * 获取设备升级进度
 * @param {number} id - 设备ID
 * @returns {Promise}
 */
export function getFirmwareProgress(id) {
  return request({
    url: `/admin/device/${id}/firmware/progress`,
    method: 'get'
  })
}

/**
 * 获取设备操作日志
 * @param {number} id - 设备ID
 * @param {Object} params - 查询参数
 * @returns {Promise}
 */
export function getDeviceLogs(id, params = {}) {
  return request({
    url: `/admin/device/${id}/logs`,
    method: 'get',
    params
  })
}

/**
 * 批量操作设备
 * @param {Array} deviceIds - 设备ID数组
 * @param {string} operation - 操作类型 (enable, disable, delete, bind, unbind)
 * @param {Object} data - 操作数据
 * @returns {Promise}
 */
export function batchDeviceOperation(deviceIds, operation, data = {}) {
  return request({
    url: '/admin/device/batch',
    method: 'post',
    data: {
      deviceIds,
      operation,
      ...data
    }
  })
}

/**
 * 获取设备统计信息
 * @returns {Promise}
 */
export function getDeviceStatistics() {
  return request({
    url: '/admin/device/statistics',
    method: 'get'
  })
}

export function getProfileByDeviceCode(deviceCode) {
  return request({
    url: '/admin/device/profile/by-device-code',
    method: 'get',
    params: { deviceCode }
  })
}

export function bindDeviceByCode(data) {
  return request({
    url: '/admin/device/bind-by-code',
    method: 'post',
    data
  })
}

export function unbindDeviceByCode(data) {
  return request({
    url: '/admin/device/unbind-by-code',
    method: 'post',
    data
  })
}

/**
 * 获取NFC卡记录列表（分页）
 * @param {Object} params - { page, limit, keywords, cardType, bindStatus }
 */
export function getNfcCardList(params = {}) {
  return request({
    url: '/admin/nfc-card/list',
    method: 'get',
    params
  })
}

/**
 * 获取NFC卡记录详情
 */
export function getNfcCardDetail(nfcUuid) {
  return request({
    url: `/admin/nfc-card/${encodeURIComponent(nfcUuid)}`,
    method: 'get'
  })
}

/**
 * 解绑NFC卡
 */
export function unbindNfcCard(nfcUuid) {
  return request({
    url: `/admin/nfc-card/${encodeURIComponent(nfcUuid)}/unbind`,
    method: 'put'
  })
}

/**
 * 新增NFC卡记录
 * @param {Object} data - { nfcUuid, cardType, sku, userId?, deviceCode? }
 */
export function createNfcCard(data) {
  return request({
    url: '/admin/nfc-card',
    method: 'post',
    data
  })
}

/**
 * 获取指定用户的NFC卡记录列表
 * @param {number|string} userId - 用户ID
 */
export function getNfcCardsByUserId(userId) {
  return request({
    url: `/admin/nfc-card/user/${userId}`,
    method: 'get'
  })
}

/**
 * 获取设备绑定用户的智能体列表
 * @param {string} deviceId - 设备ID
 * @returns {Promise}
 */
export function getDeviceUserAgents(deviceId) {
  return request({
    url: `/admin/device/${deviceId}/agents`,
    method: 'get'
  })
}

/**
 * 实时控制设备音量（通过agentlink-server转发MCP指令到设备）
 * @param {string} deviceId - 设备ID
 * @param {number} volume - 音量值 (0-100)
 * @returns {Promise}
 */
export function controlDeviceVolume(deviceId, volume) {
  return request({
    url: `/admin/device/${deviceId}/volume`,
    method: 'post',
    data: { volume },
    timeout: 10000,
    permissionToast: false
  })
}

/**
 * 发送WiFi控制指令到设备（通过agentlink-server转发MCP指令到设备）
 * @param {string} deviceId - 设备ID
 * @param {string} action - 操作类型: get_list|add|remove|update|switch
 * @param {Object} params - 操作参数 { ssid, password }
 * @returns {Promise}
 */
export function controlDeviceWifi(deviceId, action, params = {}) {
  return request({
    url: `/admin/device/${deviceId}/wifi`,
    method: 'post',
    data: { action, ...params },
    timeout: 10000,
    permissionToast: false
  })
}
